import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FiltersSection from '../components/FiltersSection'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import axiosInstance from '../utils/axiosInstance'

const getTableColumns = (t) => [
  { key: 'school', label: t('devices.object') },
  { key: 'timestamp', label: t('logs.dateTime') },
  { key: 'message', label: t('logs.message') },
];


const DATEPICKER_PORTAL_ID = 'logs-datepicker-portal';

const Logs = ({ setIsAuthenticated, userRole, userEmail, onLogout }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const tableColumns = getTableColumns(t);
  const [schoolList, setSchoolList] = useState([]);
  const [schoolData, setSchoolData] = useState([]); 
  const [selectedSchool, setSelectedSchool] = useState('');
  const [ipMap, setIpMap] = useState({});
  const [regionMap, setRegionMap] = useState({}); 
  const [schoolRegionMap, setSchoolRegionMap] = useState({}); 
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const itemsPerPage = 50;

  const toggleSidebar = () => setSidebarVisible(!isSidebarVisible);

  const isFiltered = useMemo(() => Boolean(selectedSchool || startDate || endDate), [selectedSchool, startDate, endDate]);

  const fetchLogs = async (filters = {}, page = currentPage) => {
    try {
      const params = {
        ...filters,
        page: page,
        limit: itemsPerPage,
      };
      const response = await axiosInstance.get('/metrics/logs', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      const logsData = response.data.data || [];
      const pagination = response.data.pagination || {};
      setLogs(logsData);
      setTotalLogs(pagination.total || logsData.length);
    } catch (error) {
    }
  };

  const fetchSchoolList = async () => {
    try {
      const response = await axiosInstance.get('/metrics/school-devices', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const data = response.data.data || [];
      const uniqueSchools = [...new Set(data.map((d) => d.school))];
      const mapping = {};
      const regionMapping = {};
      const schoolRegionMap = {}; // Маппинг школа -> регион
      
      // Сначала проходим по всем данным и собираем регионы для школ
      data.forEach(({ school, ip_address, region }) => {
        // Если для школы еще нет IP, сохраняем первый встреченный
        if (!mapping[school]) {
        mapping[school] = ip_address;
        }
        regionMapping[ip_address] = region || '';
      });
      
      // Затем проходим еще раз и сохраняем регион для каждой школы
      // Приоритет - непустому региону
      data.forEach(({ school, ip_address, region }) => {
        const ip = mapping[school];
        const regionForIp = regionMapping[ip];
        // Если для школы еще нет региона или текущий непустой, сохраняем
        if (!schoolRegionMap[school] || (regionForIp && regionForIp.trim())) {
          schoolRegionMap[school] = regionForIp || '';
        }
      });
      
      // Отладка для "Средняя школа №3"
      if (schoolRegionMap['Средняя школа №3']) {
      }

      setSchoolList(uniqueSchools);
      setSchoolData(data);
      setIpMap(mapping);
      setRegionMap(regionMapping);
      // Сохраняем маппинг школы -> регион в отдельном state
      setSchoolRegionMap(schoolRegionMap);
    } catch (error) {
    }
  };


  useEffect(() => {
    fetchLogs({}, 1);
    fetchSchoolList();
  }, []);

  // Автоматическая фильтрация при изменении дат или школы (с небольшой задержкой)
  useEffect(() => {
    // Пропускаем автоматическую фильтрацию при первой загрузке
    if (schoolList.length === 0) return;

    const timer = setTimeout(() => {
      // Строим параметры фильтрации
      const params = {};
      if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
      const start = serializeDate(startDate, false);
      const end = serializeDate(endDate, true);
      if (start) params.start = start;
      if (end) params.end = end;

      // Применяем фильтры только если есть хотя бы один активный фильтр
      if (params.ip || params.start || params.end) {
        setCurrentPage(1);
        fetchLogs(params, 1);
      } else if (selectedSchool === '' && !startDate && !endDate) {
        // Если все фильтры сброшены, загружаем все данные
        setCurrentPage(1);
        fetchLogs({}, 1);
      }
    }, 300); // Задержка 300ms для debounce

    return () => clearTimeout(timer);
  }, [selectedSchool, startDate, endDate, ipMap, schoolList.length]);

  // Загрузка логов при изменении страницы (отдельный useEffect для избежания конфликтов)
  useEffect(() => {
    if (schoolList.length === 0) return;
    
    const params = {};
    if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
    const start = serializeDate(startDate, false);
    const end = serializeDate(endDate, true);
    if (start) params.start = start;
    if (end) params.end = end;

    fetchLogs(params, currentPage);
  }, [currentPage]);

  useEffect(() => {
    let portalRoot = document.getElementById(DATEPICKER_PORTAL_ID);
    let created = false;

    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = DATEPICKER_PORTAL_ID;
      portalRoot.style.position = 'relative';
      portalRoot.style.zIndex = '9999';
      document.body.appendChild(portalRoot);
      created = true;
    }

    return () => {
      if (created && portalRoot && portalRoot.parentNode) {
        portalRoot.parentNode.removeChild(portalRoot);
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  const serializeDate = (date, isEndDate = false) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Добавляем время: для начала дня - 00:00:00, для конца дня - 23:59:59
    // Используем формат ISO 8601 с буквой T для совместимости с Elasticsearch
    const time = isEndDate ? '18:59:59' : '00:00:00';
    return `${year}-${month}-${day}T${time}`;
  };

  const buildFilters = () => {
    const params = {};
    if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
    const start = serializeDate(startDate, false);
    const end = serializeDate(endDate, true);
    if (start) params.start = start;
    if (end) params.end = end;
    return params;
  };

  const handleApplyFilters = () => {
    const params = buildFilters();
    setCurrentPage(1);
    fetchLogs(params, 1);
  };

  const handleResetFilters = () => {
    setSelectedSchool('');
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    fetchLogs({}, 1);
  };

  const totalPages = Math.ceil(totalLogs / itemsPerPage);

  return (
    <div className="relative flex min-h-screen flex-col bg-surface-base text-muted-100">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-brand-radial" />
      <Header isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} userEmail={userEmail} userRole={userRole} />
      <div className="flex flex-1">
        <Sidebar 
          onLogout={onLogout || handleLogout} 
          userRole={userRole} 
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />

        <main className="relative flex min-w-0 flex-1 flex-col gap-8 bg-surface-base/60 px-4 py-8 transition-all duration-300 ease-brand sm:px-8 lg:px-12 overflow-visible">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-brand-linear/15 blur-3xl" />

          <header className="relative flex flex-col gap-3">
            <span className="inline-flex w-min items-center gap-2 rounded-full bg-brand-400/15 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-100">
              logs
            </span>
            <h1 className="font-display text-3xl text-white md:text-4xl">{t('logs.title')}</h1>
            <p className="text-sm text-muted-400 md:text-base">
              {t('logs.subtitle')}
            </p>
          </header>

          <FiltersSection
            schoolList={schoolList}
            selectedSchool={selectedSchool}
            onSchoolChange={setSelectedSchool}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            isFiltered={isFiltered}
            datePickerPortalId={DATEPICKER_PORTAL_ID}
                calendarClassName="logs-datepicker"
                popperClassName="logs-datepicker-popper"
          />

          <section className="relative flex flex-col gap-4 rounded-3xl border border-surface-border/50 bg-surface-raised/75 p-6 shadow-elevated backdrop-blur-xl">
            <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-display text-xl text-white">{t('logs.results')}</h2>
                <p className="text-sm text-muted-400">
                  {t('logs.foundRecords', { total: totalLogs, shown: logs.length, current: currentPage, totalPages })}
                </p>
              </div>
              
            </header>

            <div className="rounded-3xl border border-surface-border/40">
              <table className="min-w-full divide-y divide-surface-border/40 text-sm">
                <thead className="bg-surface-base/70 text-xs uppercase tracking-[0.3em] text-muted-400">
                  <tr>
                    {tableColumns.map((column) => (
                      <th key={column.key} className="px-4 py-3 text-left font-semibold">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border/30">
                  {logs.map((log, index) => {
                    // Находим школу по IP из лога
                    const logIp = log.ip || log.src_ip;
                    const schoolName = Object.keys(ipMap).find((key) => ipMap[key] === logIp) || logIp;
                    // Пытаемся получить регион сначала по IP, потом по названию школы
                    const region = regionMap[logIp] || schoolRegionMap[schoolName] || '';
                    return (
                      <tr key={`${logIp}-${log.timestamp}-${index}`} className="transition hover:bg-brand-400/10">
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-white">{schoolName}</span>
                            {region && (
                              <span className="text-xs text-muted-400 mt-0.5">{region}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-muted-300">
                          {new Date(log.timestamp).toLocaleString('ru-KZ', {
                            hour12: false,
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-4 text-muted-200">
                          <span className="inline-flex rounded-full bg-surface-base/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-500">
                            {log.level || 'info'}
                          </span>
                          <span className="mt-2 block text-muted-200">{log.message}</span>
                        </td>
                      </tr>
                    );
                  })}

                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={tableColumns.length} className="px-4 py-16 text-center text-sm text-muted-400">
                        {t('logs.noDataForPeriod')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <nav className="flex items-center justify-between gap-4 border-t border-surface-border/40 pt-4 text-sm text-muted-300">
                <span>
                  {t('logs.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <div className="inline-flex items-center gap-2 rounded-full bg-surface-base/70 p-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs transition hover:bg-brand-400/15 hover:text-white"
                    disabled={currentPage === 1}
                  >
                    <span className="material-icons text-sm">chevron_left</span>
                  </button>
                  <span className="px-3 text-xs font-semibold uppercase tracking-[0.25em] text-muted-500">
                    {currentPage}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs transition hover:bg-brand-400/15 hover:text-white"
                    disabled={currentPage === totalPages}
                  >
                    <span className="material-icons text-sm">chevron_right</span>
                  </button>
                </div>
              </nav>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Logs;